import {
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldInputText } from 'lib-knowledge-form';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  useCreateAiSettingsVersion,
  useRenameAiSettingsVersion,
} from '../hooks/useAiSettingsMutations';

const versionNameSchema = z.object({
  name: z.string().trim().min(1, 'Give the version a name'),
});

type VersionNameValues = z.infer<typeof versionNameSchema>;

/**
 * - `create`: the draft becomes the next version, and the AI keeps using the
 *   live one.
 * - `goLive`: the same, and the AI switches to it straight away.
 * - `rename`: the only change a saved version allows.
 */
export type VersionNameModalProps =
  | { mode: 'create' | 'goLive' }
  | { mode: 'rename'; versionId: string; name: string };

const copy = {
  create: {
    title: 'Create version',
    confirm: 'Create',
    loading: 'Creating...',
  },
  goLive: {
    title: 'Create and go live',
    confirm: 'Create and go live',
    loading: 'Creating and going live...',
  },
  rename: {
    title: 'Rename version',
    confirm: 'Save',
    loading: 'Saving...',
  },
};

export function VersionNameModal(props: VersionNameModalProps): JSX.Element {
  const { mode } = props;
  const { hideModal } = useModal();
  const { successNotify, errorNotify } = useNotification();
  const navigate = useNavigate();

  const [createVersion, { loading: creating }] = useCreateAiSettingsVersion();
  const [renameVersion, { loading: renaming }] = useRenameAiSettingsVersion();
  const loading = creating || renaming;

  const { control, handleSubmit, formState } = useForm<VersionNameValues>({
    defaultValues: { name: props.mode === 'rename' ? props.name : '' },
    resolver: zodResolver(versionNameSchema),
    mode: 'all',
  });

  const onSubmit = async ({ name }: VersionNameValues) => {
    if (props.mode === 'rename') {
      try {
        await renameVersion({ variables: { id: props.versionId, name } });
        successNotify({
          title: 'Version renamed',
          text: `"${props.name}" has been renamed to "${name}".`,
        });
        hideModal();
      } catch {
        errorNotify({ text: 'The version could not be renamed. Try again.' });
      }
      return;
    }

    const activate = mode === 'goLive';
    try {
      const { data } = await createVersion({ variables: { name, activate } });
      const versionId = data?.organisationCreateAiSettingsVersion?.id;
      successNotify(
        activate
          ? { title: 'Version live', text: `"${name}" is now live.` }
          : { title: 'Version created', text: `"${name}" has been saved.` },
      );
      hideModal();
      // As in the MIM settings: the draft is gone, so show what it became.
      if (versionId)
        navigate(routeGenerator.aiSuggestionsVersion({ versionId }));
    } catch {
      errorNotify({
        text: activate
          ? 'The version could not be created and made live. Try again.'
          : 'The version could not be created. Try again.',
      });
    }
  };

  return (
    <ModalWrapper>
      <ModalHead title={copy[mode].title} onClose={hideModal} />
      <ModalBody>
        <FormGrid>
          <FormField>
            <FieldInputText
              control={control}
              name="name"
              label="Version name"
              placeholder="Initial version"
              description="Give this version a name so you can recognize it in the history."
              required
            />
          </FormField>
        </FormGrid>
      </ModalBody>
      <ModalFoot
        confirmName={copy[mode].confirm}
        confirmOnClick={handleSubmit(onSubmit)}
        confirmProps={{
          isLoading: loading,
          loadingIndicator: copy[mode].loading,
          disabled: loading || !formState.isValid || !formState.isDirty,
        }}
        cancelOnClick={hideModal}
        cancelProps={{ variant: 'secondary' }}
      />
    </ModalWrapper>
  );
}
